import { createSelector, PayloadAction } from '@reduxjs/toolkit'
import { call, fork, select } from 'redux-saga/effects'

import { resetDrawableHighlightState } from './resetDrawableHighlightState'
import { RootState } from '../../../stores'
import managers from '../../lib/managers'
import { Color, Workspace } from '../../lib/toolBoxes/2D'
import { selectFocusedOverlayChunkId } from '../../slices/2D'
import {
    selectAreConflictsBeingReviewed,
    selectAreMaterialInteractionsBlocked,
    selectIsOverlaidChunkLocked,
} from '../../slices/masterSetPlan'

export const selectEditingActive = createSelector(
    (state: RootState) => state.IMUP['2D'].editingActive,
    (state) => state
)

export function* handleToggleOpeningLocationOnHover2D(
    action: PayloadAction<{ opening_location_id: number; exiting?: boolean }>
) {
    const areMaterialInteractionsBlocked: boolean = yield select(selectAreMaterialInteractionsBlocked)

    if (areMaterialInteractionsBlocked && !action.payload.exiting) return

    const paperManager = yield call(managers.get2DManager)

    if (!paperManager) return
    const workspaceTool: Workspace = yield call(paperManager.getTool, Workspace.NAME)

    const paths: paper.PathItem[] | null = yield call(
        workspaceTool.getItemsWithOpeningLocationId,
        action.payload.opening_location_id
    )

    // Return if there's no path found
    if (!paths) return

    // for headers and beams with headers the items found by opening location id
    // will be both the group with labels rectangle and the path
    // itself, we always want the path. During conflict resolution, hidden original
    // items may share the same opening_location_id as the visible conflict item,
    // so prefer visible leaf items (no children), falling back to any visible item.
    const path: paper.Item | undefined =
        paths.length === 1
            ? paths[0]
            : paths.find((item) => item.visible && !item.children) ?? paths.find((item) => item.visible)

    // Return if there's no path found
    if (!path) return

    const editingActive: boolean = yield select(selectEditingActive)

    if (editingActive) return

    const overlayChunkId = yield select(selectFocusedOverlayChunkId)
    const isOverlaidChunkLocked = yield select(selectIsOverlaidChunkLocked)
    const areConflictsBeingReviewed = yield select(selectAreConflictsBeingReviewed)

    if (!!overlayChunkId && !isOverlaidChunkLocked && !areConflictsBeingReviewed) return

    // Attach the path's animation function and highlight it if there is no animation active on the project view
    // Otherwise, clear the animation and reset the shape
    const paperScope: paper.PaperScope = yield call(workspaceTool.getPaperScope)

    if (!paperScope.project.view.onFrame && !action.payload.exiting) {
        const colorTool: Color = yield call(paperManager.getTool, Color.NAME)
        const redColor = yield call(colorTool.createColor, 'red')

        yield call(highlightPath, path, redColor)
        yield call(animatePath, path)
    } else {
        const pathLine: paper.PathItem | paper.Item = path

        yield call(removeAnimation, pathLine)

        if (pathLine.strokeColor || pathLine.fillColor) yield fork(resetDrawableHighlightState, pathLine)

        if (pathLine.strokeWidth) pathLine.strokeWidth = pathLine.data.originalStrokeWidth
    }
}

export function animatePath(path: paper.Item) {
    path.project.view.onFrame = path.data.onFrameHandler
}

export function removeAnimation(path: paper.Item) {
    path.project.view.onFrame = null
}

export function highlightPath(path: paper.Item, color: paper.Color) {
    path.strokeColor = color
}
